import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MdSmartToy, MdSend } from 'react-icons/md';
import DocumentNavigationBar from './DocumentNavigationBar';
import DocumentHeader from './DocumentHeader';
import MarkdownEditor from './MarkdownEditor';
import PreviewPane from './PreviewPane';
import QAPanel from './QAPanel';
import TableOfContents from './TableOfContents';
import SelectionPopup from './SelectionPopup';
import EditPopup from './EditPopup';
import DiffPreview from './DiffPreview';
import EditInterface from './EditInterface';
import QuickReferenceModal from './QuickReferenceModal';
import NewDocumentModal from './NewDocumentModal';
import DropIndicator from './ImageUpload/DropIndicator';
import ProgressIndicator from './ImageUpload/ProgressIndicator';
import { useDocumentResizing } from './hooks/useDocumentResizing';
import { useClass } from '../../utils/ClassContext';
import { documentEditorApi, aiGuidelineApi } from '../../utils/api/DocumentEditorApi';
import * as embeddedQuestionsApi from '../../utils/api/DocumentEditorApi/EmbeddedQuestionsApi';
import { useNotifications } from '../common/NotificationManager';
import { useImageUpload } from '../../hooks/useImageUpload';
import { OctaveProvider } from '../../contexts/OctaveContext';
import { PythonProvider } from '../../contexts/PythonContext';
import { useERDState } from '../ERDEditor/hooks/useERDState';
import RubricSidebar from '../Shared/RubricSidebar';
import SubmissionHistorySidebar from '../Shared/SubmissionHistorySidebar';
import { lookupAssignment, createSubmission, submitSubmission } from '../../utils/api/AssignmentsApi';

/**
 * Replace ERD block content in a markdown string.
 *
 * IMPORTANT: remark (used by react-markdown) appends a trailing '\n' to code
 * block values, but the raw markdown only has a single '\n' before the closing
 * fence.  Trimming both sides before matching keeps the regex correct regardless
 * of which react-markdown / remark version is in use.
 */
const applyErdOverrides = (markdownContent, overrides) => {
  let result = markdownContent;
  for (const [originalContent, newContent] of Object.entries(overrides)) {
    const originalTrimmed = originalContent.trimEnd();
    const newContentTrimmed = newContent.trimEnd();
    if (!originalTrimmed || originalTrimmed === newContentTrimmed) continue;
    // Escape regex special chars in the original content
    const escaped = originalTrimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Match any ```erd, ```erd:editor, ```erd:env, ```erd:env:editable, etc.
    const regex = new RegExp('(```erd[^\\n]*\\n)' + escaped + '(\\n```)', 'g');
    result = result.replace(regex, `$1${newContentTrimmed}$2`);
  }
  return result;
};

const DocumentEditor = ({ username, userRole, isPublicView = false, apiKey = null, assignmentId = null, componentId = null, documentId: initialDocumentId = null, onBack = null }) => {
  // Check if user is student for restricted interface
  const isStudent = userRole !== 'Instructor' && userRole !== 'TA';
  // Public users are view-only
  const isPublic = isPublicView || userRole === 'Public';

  const { getClassContext } = useClass();
  const { showError, showSuccess, showWarning, showInfo } = useNotifications();

  const containerRef = useRef(null);
  const textAreaRef = useRef(null);

  // Resizing hook
  const { leftPanelWidth, rightPanelWidth, tocWidth, handleResizeStart } = useDocumentResizing(containerRef);

  // Current document state
  const [currentDocument, setCurrentDocument] = useState(null);

  // Rubric sidebar state
  const [showRubricSidebar, setShowRubricSidebar] = useState(false);
  const [showSubmissionsSidebar, setShowSubmissionsSidebar] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignmentData, setAssignmentData] = useState(null);

  // Auto-detected assignment context (when opened from main nav, not assignment flow)
  const [detectedAssignmentId, setDetectedAssignmentId] = useState(null);
  const [detectedComponentId, setDetectedComponentId] = useState(null);
  const effectiveAssignmentId = assignmentId || detectedAssignmentId;
  const effectiveComponentId = componentId || detectedComponentId;

  // Compute due date status for submit button
  const getDueDateStatus = () => {
    if (!assignmentData?.due_date) return { canSubmit: true, isLate: false, isLocked: false };
    const now = new Date();
    const due = new Date(assignmentData.due_date);
    if (now <= due) return { canSubmit: true, isLate: false, isLocked: false };

    const allowLate = assignmentData.allow_late_submission !== false;
    const lockAfter = assignmentData.lock_after_due_date === true;

    if (lockAfter && !allowLate) return { canSubmit: false, isLate: false, isLocked: true };
    if (!allowLate) return { canSubmit: false, isLate: false, isLocked: true };

    if (assignmentData.late_due_date) {
      const lateDue = new Date(assignmentData.late_due_date);
      if (now > lateDue) return { canSubmit: false, isLate: false, isLocked: true };
    }

    return { canSubmit: true, isLate: true, isLocked: false };
  };

  const dueDateStatus = getDueDateStatus();

  // Handle assignment submission - always creates a NEW submission
  const handleSubmitAssignment = async () => {
    const classContext = getClassContext();
    if (!classContext || !effectiveAssignmentId) return;

    setIsSubmitting(true);
    try {
      // Always create a new submission (new attempt)
      const createResult = await createSubmission({
        assignment_id: effectiveAssignmentId,
        submission_type: 'document',
        content: {
          markdown: documentContent,
          document_id: currentDocument?.id,
          title: documentTitle,
          student_progress: studentProgress
        },
        status: 'draft'
      }, classContext);

      if (!createResult.success) throw new Error(createResult.error || 'Failed to create submission');

      const submitResult = await submitSubmission(createResult.submission_id, classContext);
      if (!submitResult.success) throw new Error(submitResult.error || 'Failed to submit');

      const lateMsg = submitResult.is_late ? ' (Late submission - penalty may apply)' : '';
      showSuccess(`Assignment submitted successfully!${lateMsg}`);
    } catch (error) {
      console.error('Submission error:', error);
      showError(error.message || 'Failed to submit assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ERD state (shared between ERDSnippet and QAPanel)
  // Pass documentId and source='embedded' for activity tracking
  const erdState = useERDState(username, apiKey, null, getClassContext, currentDocument?.id, 'embedded');

  // Right panel: top-level tab ('qa' | 'ai-guide') and ERD context from snippets
  const [activeRightPanel, setActiveRightPanel] = useState('ai-guide');
  const [activeERDContext, setActiveERDContext] = useState(null);

  const [feedbackRequestCount, setFeedbackRequestCount] = useState(0);

  const feedbackEnvRef = useRef(null);
  const feedbackReqsRef = useRef(null);

  const handleRequestFeedback = useCallback((envId, requirements) => {
    if (envId) feedbackEnvRef.current = envId;
    if (requirements) feedbackReqsRef.current = requirements;
    setActiveRightPanel('ai-guide');
    setFeedbackRequestCount(n => n + 1);
  }, []);

  // Clear AI Guide feedback state whenever the user navigates to a different document
  useEffect(() => {
    if (!currentDocument?.id) return;
    erdState.setFeedbackItems([]);
    erdState.setFeedbackId(null);
    erdState.setIsGettingFeedback(false);
    erdState.setScaffoldStages?.([]);
    erdState.setStage0Type?.(null);
    feedbackEnvRef.current = null;
    feedbackReqsRef.current = null;
    setFeedbackRequestCount(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDocument?.id]);

  // Stable classContext object — new object from getClassContext() on every render would
  // cause renderCodeBlock in PreviewPane to recompute its useMemo on every parent render.
  const stableClassContext = useMemo(() => getClassContext(), [getClassContext]);

  // AI Guideline: track the section whose guideline is being edited
  const [guidelineSection, setGuidelineSection] = useState(null); // { section_id, name }

  /**
   * Called from TableOfContents when educator right-clicks a section → Edit AI Guideline.
   * Fetches (or creates) the guideline document and opens it in the editor as a regular doc.
   */
  const handleEditGuideline = useCallback(async (section) => {
    try {
      // Use guideline already in the TOC data if available, otherwise fetch
      const existing = section.sectionGuideline;
      let guideline = existing;
      if (!guideline) {
        const classContext = getClassContext();
        const res = await aiGuidelineApi.getGuideline(section.section_id, classContext);
        guideline = res?.guideline || null;
      }

      const guidelineDoc = guideline || {
        document_id: null,
        title: `AI Guideline — ${section.name}`,
        content: `# ${section.name}\n\n## Learning Objectives\n\n## MCQ Bank\n\n## Scaffolding Rules\n`,
        document_type: 'ai_guideline',
        section_id: section.section_id,
      };

      setGuidelineSection({ section_id: section.section_id, name: section.name });
      handleDocumentOpen(guidelineDoc);
    } catch (err) {
      console.error('Failed to load AI guideline:', err);
    }
  }, [getClassContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect ERD notation changes made via the VisualEditor in preview mode.
  // Keyed by the original ERD block content (as parsed from markdown).
  // Applied to documentContent before save and when switching to the markdown editor tab.
  const erdNotationOverridesRef = useRef({});

  const handleErdNotationChange = useCallback((originalContent, newNotation) => {
    erdNotationOverridesRef.current[originalContent] = newNotation;
  }, []);

  // Debug: Log whenever currentDocument changes
  useEffect(() => {
    console.log('[DocumentEditor] currentDocument state changed:', {
      hasDocument: !!currentDocument,
      id: currentDocument?.id,
      title: currentDocument?.title,
      timestamp: new Date().toISOString()
    });
  }, [currentDocument]);

  // Auto-load document when opened from an assignment (initialDocumentId prop)
  useEffect(() => {
    if (!initialDocumentId || currentDocument) return;
    const classContext = getClassContext();
    if (!classContext) return;

    documentEditorApi.getDocument(initialDocumentId, classContext)
      .then(data => {
        if (data.success && data.document) {
          handleDocumentOpen({
            id: data.document.document_id,
            document_id: data.document.document_id,
            title: data.document.title,
            content: data.document.content,
            environment_id: data.document.environment_id,
            section_id: data.document.section_id,
            assignment_id: data.document.assignment_id
          });
        }
      })
      .catch(err => console.error('[DocumentEditor] Failed to auto-load assignment document:', err));
  }, [initialDocumentId]);

  // Document state
  const [documentContent, setDocumentContent] = useState(isStudent ? '' : '');
  const [documentTitle, setDocumentTitle] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const [activeTab, setActiveTab] = useState('markdown'); // 'markdown' or 'preview'
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [studentProgress, setStudentProgress] = useState({}); // Track student's answers to embedded questions
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [lastSavedContent, setLastSavedContent] = useState(''); // Track content at last save
  const [lastSavedTitle, setLastSavedTitle] = useState(''); // Track title at last save
  const [tocRefreshTrigger, setTocRefreshTrigger] = useState(0);
  const [lastCreatedSection, setLastCreatedSection] = useState(null);

  // Pending edit state for inline diff preview
  const [pendingEdit, setPendingEdit] = useState(null); // { originalText, editedText, range }

  // Inline edit interface state
  const [showInlineEdit, setShowInlineEdit] = useState(false);
  const [inlineWidgetPosition, setInlineWidgetPosition] = useState(null); // Cursor position for widget

  // Image upload functionality
  const {
    isDragging,
    dragPosition,
    isValidFile,
    uploadStatus,
    uploadProgress,
    uploadMessage,
    currentFilename,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    uploadImage
  } = useImageUpload({
    currentDocument,
    getClassContext,
    onImageInserted: (markdownText, insertPosition, metadata) => {
      // Insert image markdown at cursor position
      const textarea = textAreaRef.current;
      if (textarea) {
        // Always try to insert at cursor position
        const cursorPos = textarea.selectionStart !== undefined ? textarea.selectionStart : documentContent.length;
        const before = documentContent.substring(0, cursorPos);
        const after = documentContent.substring(cursorPos);
        const newContent = `${before}\n\n${markdownText}\n\n${after}`;
        setDocumentContent(newContent);
        
        // Move cursor to after the inserted image
        setTimeout(() => {
          const newCursorPos = cursorPos + markdownText.length + 4; // +4 for the newlines
          textarea.selectionStart = textarea.selectionEnd = newCursorPos;
          textarea.focus();
        }, 0);
      } else {
        // Fallback: append to end
        setDocumentContent(prev => `${prev}\n\n${markdownText}\n\n`);
      }
      
      // Show notification based on upload result
      if (metadata?.deduplicated) {
        showInfo('Image already exists - reusing existing copy');
      } else {
        showSuccess('Image uploaded successfully');
      }
      
      // Refresh TOC if needed
      setTocRefreshTrigger(prev => prev + 1);
    },
    disabled: isStudent && !currentDocument // Allow students to upload only when editing documents
  });

  // Load student's progress on embedded questions
  const loadStudentProgress = async (documentId) => {
    if (!documentId) return;

    const classContext = getClassContext();
    if (!classContext || !classContext.class_id) {
      // No class context - this is expected for public documents or when not in a class
      // Just set empty progress and don't log an error
      setStudentProgress({});
      return;
    }

    try {
      const data = await embeddedQuestionsApi.getStudentAnswers(documentId, classContext);
      if (data.success) {
        // Set progress to empty object if no progress exists (first time accessing document)
        setStudentProgress(data.progress || {});
        console.log('[DocumentEditor] Student progress loaded:', data.progress);
      }
    } catch (error) {
      // This is expected if document has no embedded questions or student hasn't answered yet
      // Just set empty progress and continue
      setStudentProgress({});
      // Only log if it's not the expected "no answers yet" case
      if (!error.message.includes('No answers found')) {
        console.warn('[DocumentEditor] Could not load student progress:', error.message);
      }
    }
  };

  // Handle answer submission for embedded questions
  const handleSubmitAnswer = useCallback(async (questionId, answerData, questionType, rubric) => {
    if (!currentDocument?.id) {
      showError('No document selected');
      return;
    }

    try {
      const result = await embeddedQuestionsApi.submitAnswer({
        documentId: currentDocument.id,
        questionId,
        questionType,
        answerData,
        rubric
      }, getClassContext());

      if (result.success) {
        // Update progress with new answer
        setStudentProgress(prev => ({
          ...prev,
          [questionId]: {
            answered: true,
            is_correct: result.validation.is_correct,
            attempt_number: result.attempt_number,
            last_submitted: new Date().toISOString()
          }
        }));

        // Feedback is shown in the question component itself
        // No toast notification needed
        return result;
      }
    } catch (error) {
      console.error('[DocumentEditor] Failed to submit answer:', error);
      showError('Failed to submit answer. Please try again.');
      throw error;
    }
  }, [currentDocument?.id, showError, getClassContext]);

  // Handle document opening from browser
  const handleDocumentOpen = async (document) => {
    console.log('[DocumentEditor] handleDocumentOpen called with document:', {
      rawDocument: document,
      hasId: !!document.id,
      hasDocumentId: !!document.document_id,
      id: document.id,
      document_id: document.document_id,
      title: document.title
    });

    // Normalize document object - TableOfContents uses document_id, others use id
    const normalizedDocument = {
      ...document,
      id: document.id || document.document_id  // Ensure consistent id field
    };

    console.log('[DocumentEditor] Normalized document:', {
      id: normalizedDocument.id,
      title: normalizedDocument.title,
      hasAllFields: !!normalizedDocument.id && !!normalizedDocument.title
    });

    setCurrentDocument(normalizedDocument);
    const loadedTitle = document.title || document.name || 'Untitled Document';
    setDocumentTitle(loadedTitle);

    try {
      // Load full document content from backend
      const documentId = normalizedDocument.id;
      console.log('[DocumentEditor] Loading document content for ID:', documentId);

      if (documentId) {
        const data = await documentEditorApi.getDocument(documentId, getClassContext());
        console.log('[DocumentEditor] getDocument response:', {
          success: data.success,
          hasDocument: !!data.document,
          documentId: data.document?.document_id,
          contentLength: data.document?.content?.length
        });

        if (data.success) {
          const loadedContent = data.document.content || '';
          setDocumentContent(loadedContent);
          // Track the loaded state as the last saved state
          setLastSavedContent(loadedContent);
          setLastSavedTitle(loadedTitle);
          // Set last saved time from document's updated_at timestamp
          if (data.document.meta && data.document.meta.updated_at) {
            setLastSavedTime(new Date(data.document.meta.updated_at));
          } else {
            // Fallback to document modified time if available
            setLastSavedTime(document.modified ? new Date(document.modified) : null);
          }

          // Merge full API response into currentDocument so folder_id and other
          // backend fields (needed for RAG) are available in buildAIContext
          setCurrentDocument(prev => ({
            ...prev,
            folder_id: data.document.folder_id || prev?.folder_id || null,
            section_id: data.document.section_id || prev?.section_id || null,
          }));

          console.log('[DocumentEditor] Document loaded successfully, currentDocument state:', {
            id: normalizedDocument.id,
            title: loadedTitle,
            folder_id: data.document.folder_id || null
          });

          // Load student progress for embedded questions
          if (isStudent) {
            loadStudentProgress(documentId);
          }
        } else {
          // Fallback to preview if full content load fails
          const loadedContent = document.content || document.preview || '';
          setDocumentContent(loadedContent);
          setLastSavedContent(loadedContent);
          setLastSavedTitle(loadedTitle);
          setLastSavedTime(document.modified ? new Date(document.modified) : null);
        }
      } else {
        // Fallback to existing content if no ID
        const loadedContent = document.content || document.preview || '';
        setDocumentContent(loadedContent);
        setLastSavedContent(loadedContent);
        setLastSavedTitle(loadedTitle);
        setLastSavedTime(document.modified ? new Date(document.modified) : null);
      }
    } catch (error) {
      console.error('Failed to load document content:', error);
      // Fallback to existing content on error
      const loadedContent = document.content || document.preview || '';
      setDocumentContent(loadedContent);
      setLastSavedContent(loadedContent);
      setLastSavedTitle(loadedTitle);
      setLastSavedTime(document.modified ? new Date(document.modified) : null);
    }
    
    setGeneratedQuestions([]);
    setActiveTab('markdown');

    // Clear guideline context if a regular document is opened
    if (document.document_type !== 'ai_guideline') {
      setGuidelineSection(null);
    }

    // Auto-detect assignment linkage (always runs regardless of assignmentId prop)
    {
      const docId = normalizedDocument.id;
      if (document.assignment_id) {
        // Document already has assignment_id from backend
        setDetectedAssignmentId(document.assignment_id);
        // Try to find the matching component
        try {
          const classContext = getClassContext();
          if (classContext) {
            const result = await lookupAssignment({ document_id: docId }, classContext);
            if (result.success && result.assignment) {
              setAssignmentData(result.assignment);
              const comp = (result.assignment.components || []).find(
                c => c.content_ref?.document_id === docId
              );
              setDetectedComponentId(comp?.component_id || null);
            }
          }
        } catch { /* ignore lookup errors */ }
      } else if (docId) {
        // No assignment_id in document data — do a lookup
        try {
          const classContext = getClassContext();
          if (classContext) {
            const result = await lookupAssignment({ document_id: docId }, classContext);
            if (result.success && result.assignment) {
              setDetectedAssignmentId(result.assignment.assignment_id);
              setAssignmentData(result.assignment);
              const comp = (result.assignment.components || []).find(
                c => c.content_ref?.document_id === docId
              );
              setDetectedComponentId(comp?.component_id || null);
            } else {
              setDetectedAssignmentId(null);
              setAssignmentData(null);
              setDetectedComponentId(null);
            }
          }
        } catch { /* ignore lookup errors */ }
      }
    }
  };

  // Handle new document creation
  const handleNewDocument = (document = null) => {
    if (document) {
      // Auto-select newly created document for editing
      setCurrentDocument(document);
      const newTitle = document.title;
      const newContent = document.content || '';
      setDocumentTitle(newTitle);
      setDocumentContent(newContent);
      // Track the newly created document's state
      setLastSavedContent(newContent);
      setLastSavedTitle(newTitle);
      // Set last saved time for newly created document
      setLastSavedTime(document.created ? new Date(document.created) : new Date());
      // Store section for auto-expansion if document was created in a section
      setLastCreatedSection(document.section_id || null);
      // Trigger TOC refresh since a new document was created
      setTocRefreshTrigger(prev => prev + 1);
      // Clear the section after a short delay to avoid persistent auto-expansion
      setTimeout(() => setLastCreatedSection(null), 1000);
    } else {
      // Create blank new document
      setCurrentDocument(null);
      setDocumentTitle('');
      setDocumentContent('');
      setLastSavedContent('');
      setLastSavedTitle('');
      setLastSavedTime(null); // Clear last saved time for new blank document
      setLastCreatedSection(null);
      setDetectedAssignmentId(null);
      setDetectedComponentId(null);
    }
    setGeneratedQuestions([]);
    setActiveTab('markdown');
  };

  
  // Scroll synchronization state - maintains scroll position across tabs
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollTimeoutRef = useRef(null);
  const lastScrollSourceRef = useRef(null);

  // Handle tab switching and maintain scroll position
  const handleTabSwitch = (tab) => {
    // When switching to the markdown editor, flush any pending ERD notation changes so
    // the raw text the instructor sees is up to date with what they edited visually.
    if (tab === 'markdown') {
      const overrides = erdNotationOverridesRef.current;
      if (Object.keys(overrides).length > 0) {
        setDocumentContent(prev => applyErdOverrides(prev, overrides));
        erdNotationOverridesRef.current = {};
      }
    }

    setActiveTab(tab);
    // Clear popups when switching tabs
    setShowSelectionPopup(false);
    setShowEditPopup(false);
    setEditPrompt('');
    setSelectedText('');
    setSelectionRange(null);

    // The scroll position will be automatically applied to the new tab
    // because both components listen to the same scrollPosition state
  };

  // Handle scroll synchronization - throttled updates for smooth scrolling
  const handleScroll = (scrollPercentage, source) => {
    // Only update if the change is significant enough or enough time has passed
    const now = Date.now();
    const lastUpdate = scrollTimeoutRef.lastUpdate || 0;
    const timeDiff = now - lastUpdate;
    const scrollDiff = Math.abs(scrollPercentage - scrollPosition);
    
    // Update immediately if significant scroll change or enough time has passed
    if (scrollDiff > 0.01 || timeDiff > 50) {
      setScrollPosition(scrollPercentage);
      lastScrollSourceRef.current = source;
      scrollTimeoutRef.lastUpdate = now;
      
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Reset the source after a delay to allow smooth transitions
      scrollTimeoutRef.current = setTimeout(() => {
        lastScrollSourceRef.current = null;
      }, 100);
    }
  };

  // AI Chat state
  const [currentMessage, setCurrentMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSelectionPopup, setShowSelectionPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [editPrompt, setEditPrompt] = useState('');
  const [showQuickReference, setShowQuickReference] = useState(false);

  // Question context from preview pane selection
  const [questionContext, setQuestionContext] = useState('');

  // Save modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  
  // TOC visibility state
  const [tocVisible, setTocVisible] = useState(true);

  // Handle inserting runnable code block
  const handleInsertCodeBlock = () => {
    const template = `
\`\`\`matlab:runnable
% Write your code here
% Example: plot(1:10, (1:10).^2);

\`\`\`
`;

    const textarea = textAreaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const scrollTop = textarea.scrollTop;

    const newContent =
      documentContent.slice(0, cursorPos) +
      template +
      documentContent.slice(cursorPos);

    setDocumentContent(newContent);

    // Focus back on textarea and preserve scroll position
    setTimeout(() => {
      textarea.focus();
      textarea.scrollTop = scrollTop;
      // Place cursor on the line after the first comment
      const newCursorPos = cursorPos + 29; // After "% Write your code here\n"
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Handle text selection for editing
  const handleTextSelection = () => {
    // Only allow text selection editing in markdown mode
    if (activeTab !== 'markdown') return;

    const textarea = textAreaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    if (start !== end) {
      const selected = documentContent.substring(start, end);
      setSelectedText(selected);
      setSelectionRange({ start, end });

      // Show inline edit interface at selection end position
      setShowInlineEdit(true);
      setInlineWidgetPosition(end); // Place widget at end of selection
      setPendingEdit(null); // Clear any pending edits
    } else {
      // Clear when no text is selected
      setShowInlineEdit(false);
      setInlineWidgetPosition(null);
      setPendingEdit(null);
      setSelectedText('');
      setSelectionRange(null);
    }
  };

  // Build context for AI requests
  const buildAIContext = (requestSource, selectedContent = '') => ({
    requestSource,
    documentState: {
      content: documentContent,
      title: documentTitle,
      hasContent: Boolean(documentContent.trim()),
      hasTitle: Boolean(documentTitle.trim()),
      questions: generatedQuestions || []
    },
    currentDocumentId: currentDocument?.id || null,
    // Pass all IDs so the backend can inject RAG from attached reference files
    documentId: currentDocument?.id || currentDocument?.document_id || null,
    folderId: currentDocument?.folder_id || null,
    sectionId: currentDocument?.section_id || guidelineSection?.section_id || null,
    selectedText: selectedContent || selectedText,
    author: username
  });

  // Send message to AI for editing
  const handleSendMessage = async (message = currentMessage, isEdit = false, selectedContent = '') => {
    if (!message.trim() && !isEdit) return;

    setCurrentMessage('');
    setIsProcessing(true);

    try {
      if (isEdit) {
        // Handle AI editing using the unified API
        const context = buildAIContext('selection', selectedContent);
        const data = await documentEditorApi.unifiedAI(message, context, getClassContext());

        if (data.success) {
          // Handle response flags
          if (data.flags && data.flags.includes('off_topic')) {
            showWarning(data.response || 'Request not relevant to document editing.', {
              title: 'Off-Topic Request'
            });
            return;
          }

          // Show inline diff preview instead of applying immediately
          if (selectionRange) {
            const editedText = data.editedText || data.response;

            // Store pending edit for review
            setPendingEdit({
              originalText: selectedContent,
              editedText: editedText,
              range: selectionRange
            });

            // Hide inline edit interface
            setShowInlineEdit(false);
            setShowEditPopup(false);
            setEditPrompt('');
          }
        } else {
          throw new Error(data.error || 'Failed to edit text');
        }
      }
    } catch (error) {
      console.error('AI request failed:', error);
      showError(`AI Error: ${error.message}`, {
        title: 'AI Request Failed'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Send AI message using unified endpoint
  const handleSendAIMessage = async (message) => {
    const messageToSend = message || currentMessage;
    if (!messageToSend || typeof messageToSend !== 'string' || !messageToSend.trim()) return;

    // Clear message immediately
    setCurrentMessage('');
    setIsProcessing(true);

    try {
      const context = buildAIContext('chat');
      const data = await documentEditorApi.unifiedAI(messageToSend, context, getClassContext());

      if (data.success) {
        console.log('AI Response data:', data);
        console.log('action:', data.action);
        console.log('flags:', data.flags);
        
        // Handle response flags
        if (data.flags && data.flags.includes('off_topic')) {
          showWarning(data.response || 'Request not relevant to document editing.', {
            title: 'Off-Topic Request'
          });
          return;
        }
        
        // Handle different actions
        if (data.action === 'generate_document' && data.response && data.title) {
          // Replace entire document with generated content
          setDocumentContent(data.response);
          setDocumentTitle(data.title);
          
          // Set generated questions if available
          if (data.questions && Array.isArray(data.questions)) {
            setGeneratedQuestions(data.questions);
          } else {
            setGeneratedQuestions([]);
          }
          
          // Check if document was already created by AI endpoint
          if (data.document_id) {
            // Document was already created by the AI endpoint, switch to the new document
            const newDocument = {
              id: data.document_id,
              title: data.title,
              content: data.response,
              author: '', // Will be filled when we fetch document details if needed
              created: new Date().toISOString(),
              modified: new Date().toISOString()
            };
            
            setCurrentDocument(newDocument);
            setLastSavedTime(new Date());
            
            // Trigger TOC refresh since new document was created
            setTocRefreshTrigger(prev => prev + 1);
            
            console.log('Switching to document created by AI endpoint:', data.document_id);
          } else {
            // Fallback: Auto-save if AI didn't create the document directly
            try {
              const saveResponse = await documentEditorApi.createDocumentWithQuestions(
                data.title,
                data.response,
                data.questions || [],
                null, // folderId 
                '', // handler (will be auto-generated)
                getClassContext()
              );
              
              if (saveResponse.success) {
                // Set the current document to the saved one
                const newDocument = {
                  id: saveResponse.document.document_id,
                  title: saveResponse.document.title,
                  content: saveResponse.document.content || data.response,
                  author: saveResponse.document.user.username,
                  created: saveResponse.document.meta.created_at,
                  modified: saveResponse.document.meta.updated_at
                };
                
                setCurrentDocument(newDocument);
                setLastSavedTime(new Date());
                
                // Trigger TOC refresh since new document was created
                setTocRefreshTrigger(prev => prev + 1);
                
                console.log('New document auto-saved successfully');
              }
            } catch (error) {
              console.error('Failed to auto-save generated document:', error);
              // Continue without saving - user can manually save later
            }
          }
        } else if (data.action === 'generate_questions' && data.questions) {
          // Save generated questions to database immediately (QA Panel)
          try {
            const documentIdToSave = currentDocument?.id || documentTitle || `temp-${Date.now()}`;
            const saveResult = await documentEditorApi.saveGeneratedQuestions(
              documentIdToSave,
              data.questions,
              getClassContext()
            );

            if (saveResult.success) {
              // Clear the generated questions state since they're now saved to database
              setGeneratedQuestions([]);

              // Show success message
              showSuccess(`Generated and saved ${data.questions.length} questions to Q&A panel.`, {
                title: 'Questions Generated & Saved'
              });

              // Refresh the Q&A panels to show the new questions
              if (window.location.pathname.includes('document-editor')) {
                // Trigger a refresh of Q&A data
                setTocRefreshTrigger(prev => prev + 1);
              }
            } else {
              // Fallback to local state if database save fails
              setGeneratedQuestions(prev => [...prev, ...data.questions]);
              showWarning(`Generated ${data.questions.length} questions but failed to save to database. They will be lost if you navigate away.`, {
                title: 'Save Failed'
              });
            }
          } catch (error) {
            console.error('Failed to save generated questions:', error);
            // Fallback to local state if save fails
            setGeneratedQuestions(prev => [...prev, ...data.questions]);
            showWarning(`Generated ${data.questions.length} questions but failed to save to database. They will be lost if you navigate away.`, {
              title: 'Save Failed'
            });
          }
        } else if (data.action === 'generate_embedded_questions' && data.response) {
          // Show diff preview for embedded questions
          setPendingEdit({
            originalText: documentContent,
            editedText: data.response,
            range: { start: 0, end: documentContent.length },
            actionType: 'generate_embedded_questions',
            metadata: { num_questions: data.num_questions }
          });

          // Clear edit interfaces
          setShowInlineEdit(false);
          setShowSelectionPopup(false);
          setShowEditPopup(false);
        } else if (data.action === 'edit_section' && data.response) {
          // Show diff preview for section edits
          setPendingEdit({
            originalText: data.original_section || documentContent,
            editedText: data.edited_section || data.response,
            range: { start: 0, end: documentContent.length },
            actionType: 'edit_section',
            metadata: {
              section_description: data.section_description,
              full_updated_content: data.response
            }
          });

          // Clear edit interfaces
          setShowInlineEdit(false);
          setShowSelectionPopup(false);
          setShowEditPopup(false);
        } else if (data.action === 'edit_document' && (data.response || data.editedContent)) {
          // Show diff preview for document edits
          const newContent = data.editedContent || data.response;

          setPendingEdit({
            originalText: documentContent,
            editedText: newContent,
            range: { start: 0, end: documentContent.length },
            actionType: 'edit_document'
          });

          // Clear edit interfaces
          setShowInlineEdit(false);
          setShowSelectionPopup(false);
          setShowEditPopup(false);
        } else if (data.action === 'edit' && (data.editedText || data.response)) {
          // Show inline diff preview instead of applying immediately
          if (selectionRange) {
            const editContent = data.editedText || data.response;

            // Store pending edit for review
            setPendingEdit({
              originalText: selectedText,
              editedText: editContent,
              range: selectionRange
            });

            // Clear all edit interfaces
            setShowInlineEdit(false);
            setShowSelectionPopup(false);
            setShowEditPopup(false);
            setEditPrompt('');
          }
        } else if (data.action === 'chat' && data.response) {
          // Build RAG debug suffix
          const rd = data.ragDebug;
          let ragInfo = '';
          if (rd) {
            if (rd.chars > 0) {
              ragInfo = `\n\n📎 RAG: ${rd.chars} chars injected from ${rd.source} (${rd.sectionId || rd.folderId || rd.documentId})`;
            } else {
              ragInfo = `\n\n⚠️ RAG: no reference file found — sectionId=${rd.sectionId} folderId=${rd.folderId} documentId=${rd.documentId}`;
            }
          }
          showInfo(data.response + ragInfo, {
            title: 'AI Response',
            duration: 8000
          });
        } else if (data.response) {
          // Fallback for any other response type - log warning and show notification
          console.warn('Unhandled AI action type:', data.action, 'defaulting to notification');
          showInfo(data.response, {
            title: 'AI Response'
          });
        }
      } else {
        throw new Error(data.error || 'Failed to get AI response');
      }
    } catch (error) {
      console.error('AI request failed:', error);
      showError(`AI Error: ${error.message}`, {
        title: 'AI Request Failed'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle selection popup actions
  const handleEditDocument = () => {
    setShowSelectionPopup(false);
    setShowEditPopup(true);
  };

  const handleEditQuestions = () => {
    setShowSelectionPopup(false);
    // Clear selection
    setSelectedText('');
    setSelectionRange(null);
  };

  // Handle AI editing
  const handleAIEdit = async () => {
    if (!selectedText || !editPrompt.trim()) return;
    await handleSendMessage(editPrompt, true, selectedText);
  };

  // Handle inline edit request
  const handleInlineEdit = async (prompt) => {
    if (!selectedText || !prompt.trim()) return;
    await handleSendMessage(prompt, true, selectedText);
  };

  // Handle copying selected text to clipboard
  const handleCopySelection = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Show success message (you can add a toast notification here)
      console.log('Text copied to clipboard');
      handleCloseInlineEdit();
    }).catch(err => {
      console.error('Failed to copy text:', err);
    });
  };

  // Handle deleting selected text
  const handleDeleteSelection = () => {
    if (!selectionRange || !documentContent) return;

    const { start, end } = selectionRange;
    const newContent = documentContent.substring(0, start) + documentContent.substring(end);
    setDocumentContent(newContent);

    // Close the interface and clear selection
    handleCloseInlineEdit();
  };

  // Handle closing inline edit interface
  const handleCloseInlineEdit = () => {
    setShowInlineEdit(false);
    setInlineWidgetPosition(null);
    setSelectedText('');
    setSelectionRange(null);
  };

  // Handle asking a question from preview mode - pass to QA Panel
  const handleAskQuestion = (selectedText) => {
    setQuestionContext(selectedText);
  };

  // Handle accepting pending edit
  const handleAcceptEdit = () => {
    if (!pendingEdit) return;

    const { editedText, range, actionType, metadata } = pendingEdit;

    // For edit_section, use the full updated content from metadata
    let newDocumentContent;
    if (actionType === 'edit_section' && metadata?.full_updated_content) {
      newDocumentContent = metadata.full_updated_content;
    } else {
      newDocumentContent =
        documentContent.substring(0, range.start) +
        editedText +
        documentContent.substring(range.end);
    }

    setDocumentContent(newDocumentContent);
    setPendingEdit(null);
    setSelectedText('');
    setSelectionRange(null);

    // Show context-appropriate success message
    if (actionType === 'generate_embedded_questions') {
      const numQuestions = metadata?.num_questions || 'multiple';
      showSuccess(`Added ${numQuestions} embedded questions to your document.`, {
        title: 'Embedded Questions Added'
      });
    } else if (actionType === 'edit_section') {
      const sectionDesc = metadata?.section_description || 'section';
      showSuccess(`Updated ${sectionDesc} successfully.`, {
        title: 'Section Updated'
      });
    } else if (actionType === 'edit_document') {
      showSuccess('Document has been updated successfully.', {
        title: 'Document Updated'
      });
    } else {
      showSuccess('Changes applied successfully!', {
        title: 'Edit Accepted'
      });
    }
  };

  // Handle declining pending edit
  const handleDeclineEdit = () => {
    setPendingEdit(null);
    setSelectedText('');
    setSelectionRange(null);

    showInfo('Changes discarded.', {
      title: 'Edit Declined'
    });
  };

  // Handle save with section selection
  const handleSaveWithSection = async (documentData) => {
    setIsSavingDocument(true);
    
    try {
      // Check for duplicate names first
      const existingDocs = await documentEditorApi.getDocuments(null, getClassContext());
      if (existingDocs.success && existingDocs.documents) {
        const duplicateName = existingDocs.documents.find(doc => 
          doc.title.toLowerCase() === documentData.name.toLowerCase()
        );
        if (duplicateName) {
          throw new Error(`A document with the name "${documentData.name}" already exists. Please choose a different name.`);
        }
      }

      const response = await documentEditorApi.createDocument(
        documentData.name,
        documentContent, // Use current content
        null, // no folder (we're using sections instead)
        documentData.sectionId,
        '', // handler (will be auto-generated)
        getClassContext()
      );
      
      if (response.success) {
        // Close modal
        setShowSaveModal(false);
        
        // Auto-select the newly created document
        const newDocument = {
          id: response.document.document_id,
          title: response.document.title,
          content: response.document.content || documentContent,
          author: response.document.user.username,
          created: response.document.meta.created_at,
          modified: response.document.meta.updated_at
        };
        
        setCurrentDocument(newDocument);
        setDocumentTitle(newDocument.title);
        setLastSavedTime(new Date());
        
        showSuccess(`Document "${documentData.name}" saved successfully!`, {
          title: 'Document Saved'
        });
      } else {
        throw new Error(response.error || 'Failed to save document');
      }
    } catch (error) {
      console.error('Failed to save document:', error);
      showError(error.message || 'Failed to save document. Please try again.', {
        title: 'Save Failed'
      });
    } finally {
      setIsSavingDocument(false);
    }
  };


  // Save document
  const handleSaveDocument = async () => {
    console.log('[DocumentEditor] handleSaveDocument called', {
      hasCurrentDocument: !!currentDocument,
      currentDocumentId: currentDocument?.id,
      documentTitle,
      contentLength: documentContent?.length
    });

    // --- AI Guideline save path ---
    if (currentDocument?.document_type === 'ai_guideline' || guidelineSection) {
      const sectionId = currentDocument?.section_id || guidelineSection?.section_id || null;
      try {
        await aiGuidelineApi.saveGuideline(sectionId, documentTitle, documentContent, getClassContext());
        setLastSavedTime(new Date());
        setLastSavedContent(documentContent);
        setLastSavedTitle(documentTitle);
        showSuccess(`AI Guideline saved!`, { title: 'Saved' });
      } catch (err) {
        showError(`Error saving AI Guideline: ${err.message}`, { title: 'Save Error' });
      }
      return;
    }

    // Apply any pending ERD notation changes before saving.
    // These accumulate when the instructor edits ERD blocks via the VisualEditor in preview mode.
    const overrides = erdNotationOverridesRef.current;
    let contentToSave = documentContent;
    if (Object.keys(overrides).length > 0) {
      contentToSave = applyErdOverrides(documentContent, overrides);
      setDocumentContent(contentToSave);   // Keep state in sync
      erdNotationOverridesRef.current = {};
    }

    if (!contentToSave.trim()) {
      showWarning('Cannot save empty document. Please add some content first.', {
        title: 'Empty Document'
      });
      return;
    }

    // If document is already selected, update it
    if (currentDocument && currentDocument.id) {
      console.log('[DocumentEditor] Updating existing document:', currentDocument.id);

      // Check if content or title has actually changed (use contentToSave which may include ERD overrides)
      const contentChanged = contentToSave !== lastSavedContent;
      const titleChanged = documentTitle !== lastSavedTitle;

      if (!contentChanged && !titleChanged) {
        showWarning('No changes have been made to the document.', {
          title: 'No Changes'
        });
        return;
      }

      try {
        // Check for duplicate names when renaming
        if (documentTitle !== currentDocument.title) {
          const existingDocs = await documentEditorApi.getDocuments(null, getClassContext());
          if (existingDocs.success && existingDocs.documents) {
            const duplicateName = existingDocs.documents.find(doc =>
              doc.title.toLowerCase() === documentTitle.toLowerCase() &&
              doc.document_id !== currentDocument.id
            );
            if (duplicateName) {
              showWarning(`A document with the name "${documentTitle}" already exists. Please choose a different name.`, {
                title: 'Duplicate Name'
              });
              return;
            }
          }
        }

        console.log('[DocumentEditor] About to call updateDocument with:', {
          documentId: currentDocument.id,
          currentDocumentFull: currentDocument,
          title: documentTitle,
          contentLength: documentContent.length,
          classContext: getClassContext()
        });

        const data = await documentEditorApi.updateDocument(currentDocument.id, documentTitle, contentToSave, null, getClassContext());

        console.log('[DocumentEditor] updateDocument response:', {
          success: data.success,
          error: data.error,
          hasDocument: !!data.document,
          returnedDocId: data.document?.document_id
        });

        if (data.success) {
          setLastSavedTime(new Date());
          // Update tracking of last saved state (use contentToSave which includes ERD overrides)
          setLastSavedContent(contentToSave);
          setLastSavedTitle(documentTitle);

          if (data.document) {
            // Normalize the returned document to ensure it has an 'id' field
            // Backend uses 'document_id', but frontend uses 'id'
            const normalizedDocument = {
              ...data.document,
              id: data.document.document_id || data.document.id || currentDocument.id // Ensure ID is preserved
            };
            console.log('[DocumentEditor] Document updated successfully, normalized document:', {
              hasId: !!normalizedDocument.id,
              id: normalizedDocument.id,
              title: normalizedDocument.title
            });
            setCurrentDocument(normalizedDocument);
          } else {
            // If no document returned, preserve existing currentDocument but update the timestamp
            console.log('[DocumentEditor] No document in response, preserving currentDocument');
            setCurrentDocument({
              ...currentDocument,
              title: documentTitle
            });
          }
          // Trigger TOC refresh since document was saved
          setTocRefreshTrigger(prev => prev + 1);
          showSuccess(`Document "${documentTitle}" saved successfully!`, {
            title: 'Document Updated'
          });
        } else {
          throw new Error(data.error || 'Failed to save document');
        }
      } catch (error) {
        console.error('Document save failed:', error);
        showError(`Error saving document: ${error.message}`, {
          title: 'Save Error'
        });
      }
    } else {
      // No document selected - show creation modal with content
      console.log('[DocumentEditor] No current document - showing save modal for new document creation');
      setShowSaveModal(true);
    }
  };

  // Download document
  const handleDownloadDocument = () => {
    const blob = new Blob([documentContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = `${documentTitle}.txt`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <OctaveProvider>
      <PythonProvider>
        <div className="h-full w-full bg-white flex flex-col overflow-hidden">
        {/* Document Navigation Bar */}
        <DocumentNavigationBar
        documentTitle={documentTitle}
        setDocumentTitle={setDocumentTitle}
        onNewDocument={handleNewDocument}
        currentDocument={currentDocument}
        lastSavedTime={lastSavedTime}
        onSave={handleSaveDocument}
        onExport={handleDownloadDocument}
        tocVisible={tocVisible}
        onToggleToc={() => setTocVisible(!tocVisible)}
        isStudent={isStudent}
        showRubric={!!effectiveAssignmentId}
        onOpenRubric={() => setShowRubricSidebar(true)}
        showSubmissions={!!effectiveAssignmentId && isStudent}
        onOpenSubmissions={() => setShowSubmissionsSidebar(true)}
        assignmentId={effectiveAssignmentId}
        isSubmitting={isSubmitting}
        onSubmitAssignment={handleSubmitAssignment}
        dueDateStatus={dueDateStatus}
      />

      {/* Main three-column container with drag-and-drop support */}
      <div 
        ref={containerRef} 
        className="flex flex-1 min-h-0 relative"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Left Column - Table of Contents */}
        <div 
          className={`toc-container flex-shrink-0 border-r border-gray-200 bg-gray-50 transition-all duration-300 ease-in-out overflow-hidden ${
            tocVisible ? 'w-80 opacity-100' : 'w-0 opacity-0'
          }`}
        >
          <div className="w-80 h-full">
            <TableOfContents onDocumentSelect={handleDocumentOpen} onEditGuideline={!isStudent && userRole !== 'TA' ? handleEditGuideline : null} isVisible={tocVisible} refreshTrigger={tocRefreshTrigger} autoExpandSection={lastCreatedSection} isStudent={isStudent} />
          </div>
        </div>

        {/* Center Column - Document Editor */}
        <div
          className="flex flex-col bg-white min-w-0"
          style={{
            width: `calc((100% - ${tocWidth}px) * ${leftPanelWidth / 100})`,
            flexShrink: 0,
            flexGrow: 0
          }}
        >
          {!isStudent && (
            <DocumentHeader
              activeTab={activeTab}
              handleTabSwitch={handleTabSwitch}
              onShowQuickReference={() => setShowQuickReference(true)}
              onImageUpload={uploadImage}
              imageUploadDisabled={uploadStatus === 'uploading'}
              onInsertCodeBlock={handleInsertCodeBlock}
              textAreaRef={textAreaRef}
              onSave={currentDocument ? handleSaveDocument : null}
              isSaving={isSavingDocument}
            />
          )}

          {/* Document Editor - Fixed height container */}
          <div className="flex-1 min-h-0 relative">
            {!isStudent && activeTab === 'markdown' ? (
              <MarkdownEditor
                documentContent={documentContent}
                setDocumentContent={setDocumentContent}
                handleTextSelection={handleTextSelection}
                textAreaRef={textAreaRef}
                scrollTop={scrollPosition}
                onScroll={(scrollPercentage) => handleScroll(scrollPercentage, 'markdown')}
                disableScroll={!!pendingEdit} // Lock scroll when there's a pending edit
                inlineWidget={
                  ((showInlineEdit && selectedText) || pendingEdit) ? {
                    position: inlineWidgetPosition || pendingEdit?.range?.end,
                    component: (
                      <>
                        {showInlineEdit && selectedText && (
                          <EditInterface
                            selectedText={selectedText}
                            onEdit={handleInlineEdit}
                            onClose={handleCloseInlineEdit}
                            onCopy={handleCopySelection}
                            onDelete={handleDeleteSelection}
                            isProcessing={isProcessing}
                          />
                        )}
                        {pendingEdit && (
                          <DiffPreview
                            originalText={pendingEdit.originalText}
                            editedText={pendingEdit.editedText}
                            onAccept={handleAcceptEdit}
                            onDecline={handleDeclineEdit}
                            editType={pendingEdit.actionType}
                            sectionDescription={pendingEdit.metadata?.section_description}
                          />
                        )}
                      </>
                    )
                  } : null
                }
                onWidgetClose={handleCloseInlineEdit}
              />
            ) : (
              <PreviewPane
                documentContent={documentContent}
                scrollTop={scrollPosition}
                onScroll={(scrollPercentage) => handleScroll(scrollPercentage, 'preview')}
                isStudent={isStudent}
                onAskQuestion={handleAskQuestion}
                studentProgress={studentProgress}
                onSubmitAnswer={handleSubmitAnswer}
                documentId={currentDocument?.id}
                classContext={stableClassContext}
                erdState={erdState}
                environmentId={currentDocument?.environment_id || null}
                username={username}
                onNotationChange={!isStudent ? handleErdNotationChange : null}
                onRequestFeedback={handleRequestFeedback}
              />
            )}

            {/* Loading Overlay */}
            {isProcessing && (
              <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-20">
                <div className="bg-white rounded-xl shadow-xl border border-gray-200/60 p-8 flex flex-col items-center space-y-6 max-w-md mx-4">
                  <div className="relative">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                        <MdSmartToy className="text-white" size={12} />
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">AI is working on your request</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Please wait while we process your request. This may take a moment.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Selection popup - shows first when text is selected */}
            {activeTab === 'markdown' && (
              <SelectionPopup
                showPopup={showSelectionPopup}
                setShowPopup={setShowSelectionPopup}
                popupPosition={popupPosition}
                selectedText={selectedText}
                onEditDocument={handleEditDocument}
                onEditQuestions={handleEditQuestions}
              />
            )}

            {/* AI Edit Popup - shows after clicking "Edit Document" */}
            {activeTab === 'markdown' && (
              <EditPopup
                showPopup={showEditPopup}
                setShowPopup={setShowEditPopup}
                popupPosition={popupPosition}
                selectedText={selectedText}
                editPrompt={editPrompt}
                setEditPrompt={setEditPrompt}
                handleAIEdit={handleAIEdit}
                isProcessing={isProcessing}
              />
            )}
          </div>

          {/* AI Chat Footer - Only for instructors/TAs */}
          {!isStudent && (
            <div className="min-h-16 max-h-24 px-4 bg-gray-50 border-t border-gray-200/60 flex items-center gap-3 flex-shrink-0 overflow-hidden">
            <div className="flex-1 relative flex items-center min-h-16">
              <textarea
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder={!currentDocument ? "Create or select a document to use AI Assistant..." : "Message AI to generate, edit, or ask questions..."}
                className="w-full pr-12 pl-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 text-sm resize-none min-h-[2.5rem] max-h-[4.5rem] placeholder-gray-400 transition-all duration-200 overflow-y-hidden disabled:bg-gray-100 disabled:cursor-not-allowed"
                rows={1}
                style={{ overflowY: currentMessage.split('\n').length > 2 || (currentMessage.length > 0 && currentMessage.split('\n').some(line => line.length > 60)) ? 'auto' : 'hidden' }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  const newHeight = Math.min(e.target.scrollHeight, 72); // 72px = 4.5rem max height
                  e.target.style.height = newHeight + 'px';
                  // Show scrollbar only if content exceeds visible area
                  e.target.style.overflowY = e.target.scrollHeight > 72 ? 'auto' : 'hidden';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isProcessing && currentMessage.trim() && currentDocument) {
                    e.preventDefault();
                    handleSendAIMessage();
                  }
                }}
                disabled={isProcessing || !currentDocument}
              />
              <button
                onClick={() => handleSendAIMessage()}
                disabled={isProcessing || !currentMessage.trim() || !currentDocument}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all duration-200 ${
                  currentMessage.trim() && !isProcessing && currentDocument
                    ? 'text-white bg-black hover:bg-gray-800'
                    : 'text-gray-400 bg-transparent cursor-not-allowed'
                }`}
              >
                {isProcessing ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <MdSend size={16} />
                )}
              </button>
            </div>
            <div className="text-xs text-gray-400 hidden sm:block shrink-0">
              {currentDocument ? (
                <>
                  {documentContent.length} chars • {documentContent.split(' ').filter(word => word.length > 0).length} words
                  {selectedText && (
                    <span className="ml-2">• {selectedText.length} selected</span>
                  )}
                </>
              ) : (
                <span className="text-gray-400">No document selected</span>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Horizontal Resize Handle */}
        <div
          className="w-2 bg-gray-300 hover:bg-gray-400 active:bg-gray-500 cursor-col-resize flex items-center justify-center transition-colors duration-200 group self-stretch"
          onMouseDown={handleResizeStart}
        >
          <div className="w-1 h-16 bg-gray-500 rounded-full group-hover:bg-gray-600 transition-colors duration-200"></div>
        </div>

        {/* Right Panel (Q&A Panel only) */}
        <div
          className="flex flex-col min-w-0"
          style={{
            width: `calc((100% - ${tocWidth}px) * ${rightPanelWidth / 100})`,
            flexShrink: 0,
            flexGrow: 0
          }}
        >
          <QAPanel
            documentContent={documentContent}
            documentTitle={documentTitle}
            currentDocument={currentDocument}
            userRole={userRole}
            qaApi={documentEditorApi}
            getClassContext={getClassContext}
            generatedQuestions={generatedQuestions}
            setGeneratedQuestions={setGeneratedQuestions}
            isStudent={isStudent}
            isPublicView={isPublic}
            selectedContext={questionContext}
            erdState={erdState}
            apiKey={apiKey}
            username={username}
            activePanel={activeRightPanel}
            onPanelChange={setActiveRightPanel}
            erdContext={activeERDContext}
            feedbackRequestCount={feedbackRequestCount}
            feedbackEnvRef={feedbackEnvRef}
            feedbackReqsRef={feedbackReqsRef}
            sectionId={currentDocument?.section_id || null}
            sectionName={currentDocument?.section_name || null}
          />
        </div>
      </div>

      {/* Quick Reference Modal */}
      <QuickReferenceModal
        isOpen={showQuickReference}
        onClose={() => setShowQuickReference(false)}
      />
      
      {/* Save Document Modal */}
      <NewDocumentModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onConfirm={handleSaveWithSection}
        isCreating={isSavingDocument}
        title="Save Document"
        defaultFileName={!isStudent && documentTitle ? documentTitle : ''}
      />


      {/* Rubric Sidebar */}
      {effectiveAssignmentId && (
        <RubricSidebar
          assignmentId={effectiveAssignmentId}
          componentId={effectiveComponentId}
          classContext={getClassContext()}
          isOpen={showRubricSidebar}
          onClose={() => setShowRubricSidebar(false)}
          isInstructor={!isStudent}
        />
      )}

      {/* Submission History Sidebar */}
      {effectiveAssignmentId && (
        <SubmissionHistorySidebar
          assignmentId={effectiveAssignmentId}
          classContext={getClassContext()}
          username={username}
          isOpen={showSubmissionsSidebar}
          onClose={() => setShowSubmissionsSidebar(false)}
          submissionType="document"
        />
      )}

      {/* Image Upload Visual Indicators */}
      <DropIndicator
        visible={isDragging}
        position={dragPosition}
        isDragOver={isDragging}
        isValidFile={isValidFile}
      />
      
      <ProgressIndicator
        visible={uploadStatus !== 'idle'}
        progress={uploadProgress}
        status={uploadStatus}
        message={uploadMessage}
        filename={currentFilename}
      />
      </div>
      </PythonProvider>
    </OctaveProvider>
  );
};

export default DocumentEditor;